/**
 * Super Admin Self-Service Ops panel (Settings → Admin Ops).
 *
 * Visible only to super_admin. Provides:
 *   • RC Operations (tenant metadata, default/allowed from numbers, reconnect)
 *   • Feature Flags (persisted in feature_flags collection)
 *   • Secrets Health (presence chips only — never renders secret VALUES)
 *
 * NOTE (Feb 2026 IA cleanup): the former "Team & Roles" sub-section that
 * lived here was removed to eliminate duplicate user-management surfaces.
 * The canonical location for user/role management is now
 *   Settings → Users & Access → Team Members.
 * Legacy deep-links `?section=ops_users` silently redirect there.
 *
 * All mutations call /api/admin/* and are audited on the backend.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Shield, Zap, ToggleRight, KeyRound, RefreshCw, Check, X, AlertTriangle, Inbox, Download, Search, UserPlus, Eye, EyeOff } from "lucide-react";

function authHdr(token) {
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

function pickErr(e, fallback) {
  const d = e?.response?.data || {};
  return d.detail || d.message || fallback;
}

/* ── Team & Roles Section (REMOVED Feb 2026 IA cleanup) ─────────────────
 * The user list + role-change UI previously rendered here duplicated the
 * canonical surface at Settings → Users & Access → Team Members. The
 * single source of truth is now `Settings.jsx` (section key "members").
 * Any deep-link hitting `?section=ops_users` is silently redirected to
 * `?section=members` by the Settings shell.
 * ────────────────────────────────────────────────────────────────────── */

/* ── Section 2: RC Operations ────────────────────────────────────── */
function RCOperationsSection({ token }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [defaultFrom, setDefaultFrom] = useState("");
  const [allowed, setAllowed] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rc/tenant`, { headers: authHdr(token) });
      setCfg(r.data);
      setTenantId(r.data.rc_tenant_id || "");
      setDefaultFrom(r.data.default_from_number || "");
      setAllowed((r.data.allowed_from_numbers || []).join(", "));
    } catch (e) {
      toast.error(pickErr(e, "Failed to load RC settings"));
    }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load() defined above; intentional single-shot mount fetch
  useEffect(() => { load(); }, []);

  const save = async (extra = {}) => {
    setSaving(true);
    const payload = { ...extra };
    if (tenantId && tenantId !== (cfg?.rc_tenant_id || "")) payload.rc_tenant_id = tenantId.trim();
    if (defaultFrom !== (cfg?.default_from_number || "")) payload.default_from_number = defaultFrom.trim();
    const allowedArr = allowed.split(",").map(s => s.trim()).filter(Boolean);
    if (JSON.stringify(allowedArr) !== JSON.stringify(cfg?.allowed_from_numbers || [])) {
      payload.allowed_from_numbers = allowedArr;
    }
    if (Object.keys(payload).length === 0) { toast.info("No changes to save"); setSaving(false); return; }
    try {
      const r = await axios.patch(`${API}/admin/rc/tenant`, payload, { headers: authHdr(token) });
      setCfg(r.data);
      toast.success("RC tenant settings saved");
      if (r.data.trigger_error) toast.error(r.data.trigger_error);
    } catch (e) {
      toast.error(pickErr(e, "Save failed"));
    }
    setSaving(false);
  };

  if (loading) return <div className="card" style={{ padding: 32, textAlign: "center", color: "#888" }} data-testid="admin-rc-loading">Loading…</div>;

  const connected = cfg?.rc_connection_status === "connected";

  return (
    <div className="card" style={{ padding: "20px 24px" }} data-testid="admin-ops-rc">
      <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 4 }}>RC Operations</h2>
      <p style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 16 }}>
        Tenant metadata + default/allowed outbound numbers. Gateway is
        <strong style={{ color: cfg?.gateway_configured ? "var(--green)" : "var(--amber)", marginLeft: 4 }}>
          {cfg?.gateway_configured ? "configured" : "unconfigured"}
        </strong>. Status: <span data-testid="admin-rc-status" style={{ color: connected ? "var(--green)" : "var(--text-tertiary)" }}>{cfg?.rc_connection_status || "disconnected"}</span>
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>RC Tenant ID</label>
          <input className="input" value={tenantId} onChange={e => setTenantId(e.target.value)}
                 placeholder="e.g. tenant_xyz123" data-testid="admin-rc-tenant-id" disabled={saving} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Default from-number</label>
          <input className="input" value={defaultFrom} onChange={e => setDefaultFrom(e.target.value)}
                 placeholder="+15555551212" data-testid="admin-rc-default-from" disabled={saving} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Allowed from-numbers (comma-separated)</label>
        <input className="input" value={allowed} onChange={e => setAllowed(e.target.value)}
               placeholder="+15551110001, +15551110002" data-testid="admin-rc-allowed" disabled={saving} />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => save()} disabled={saving} data-testid="admin-rc-save">
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button className="btn" style={{ fontSize: 12 }} onClick={() => save({ trigger_connect: true })} disabled={saving} data-testid="admin-rc-reconnect">
          Reconnect
        </button>
        {connected && (
          <button className="btn" style={{ fontSize: 12 }} onClick={() => save({ trigger_disconnect: true })} disabled={saving} data-testid="admin-rc-disconnect">
            Disconnect
          </button>
        )}
      </div>
      {cfg?.rc_last_error && (
        <div style={{ marginTop: 12, padding: 10, background: "#fff1f0", borderRadius: 6, fontSize: 11, color: "#a32d2d" }} data-testid="admin-rc-last-error">
          <AlertTriangle style={{ width: 11, height: 11, display: "inline", marginRight: 4 }} />
          Last error: {cfg.rc_last_error}
        </div>
      )}

      <BackfillAutoMatchPanel token={token} />
      <RCDurationReconcilePanel token={token} />
      <RCAudioProbePanel token={token} />
      <EhiMysqlBridgePanel token={token} />
    </div>
  );
}

/* ── RC Duration Reconcile Panel (Feb 2026) ──────────────────────────
 * Corrects stuck rc_call rows whose CRM `duration_sec = 0` but whose
 * corresponding RC call-log entry reports a positive duration. Same
 * endpoint as the console 1-liner: POST /api/admin/rc-recording-backfill/
 * reconcile-duration. Wraps it in a form so admins don't need DevTools.
 * ────────────────────────────────────────────────────────────────── */
function RCDurationReconcilePanel({ token }) {
  const [days, setDays] = useState(14);
  const [dryRun, setDryRun] = useState(true);
  const [agentId, setAgentId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const d = Number(days);
    if (!Number.isInteger(d) || d < 1 || d > 30) {
      toast.error("Days must be 1–30"); return;
    }
    setRunning(true);
    setResult(null);
    try {
      const body = { days: d, dry_run: dryRun };
      if (agentId.trim()) body.agent_id = agentId.trim();
      const r = await axios.post(
        `${API}/admin/rc-recording-backfill/reconcile-duration`,
        body, { headers: authHdr(token), timeout: 240000 },
      );
      setResult(r.data);
      toast.success(dryRun
        ? `Dry-run: ${r.data.patched} rows would be corrected`
        : `Corrected ${r.data.patched} rows`);
    } catch (e) {
      toast.error(pickErr(e, "Reconcile failed"));
    }
    setRunning(false);
  };

  return (
    <div
      style={{ marginTop: 20, paddingTop: 16, borderTop: "0.5px solid var(--border)" }}
      data-testid="admin-rc-duration-reconcile"
    >
      <h3 style={{ fontSize: 13, fontWeight: 650, marginBottom: 4 }}>Duration Reconcile (RC call-log)</h3>
      <p style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 12 }}>
        Corrects rows where WebPhone reported 0 seconds but RC&apos;s call-log
        has a positive duration (typical for calls where the rep hung up before
        the WebPhone SDK&apos;s <em>connected</em> event fired). Uses each rep&apos;s
        own RC OAuth token; reps without a token are counted as
        <code style={{ fontSize: 10 }}>skipped_no_token</code>. Idempotent — re-running
        skips already-corrected rows.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "100px 240px 1fr", gap: 12, alignItems: "end", marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Days (1–30)</label>
          <input
            type="number" min={1} max={30} className="input"
            value={days} onChange={e => setDays(e.target.value)}
            disabled={running}
            data-testid="admin-rc-recon-days"
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Agent ID (optional)</label>
          <input
            className="input" value={agentId} onChange={e => setAgentId(e.target.value)}
            placeholder="Leave blank for all reps"
            disabled={running}
            data-testid="admin-rc-recon-agent"
          />
        </div>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox" checked={dryRun}
            onChange={e => setDryRun(e.target.checked)}
            disabled={running}
            data-testid="admin-rc-recon-dryrun"
          />
          Dry-run (no writes)
        </label>
      </div>

      <button
        className="btn btn-primary" style={{ fontSize: 12 }}
        onClick={run} disabled={running}
        data-testid="admin-rc-recon-run"
      >
        {running ? "Running…" : dryRun ? "Preview" : "Run reconcile"}
      </button>

      {result && (
        <div
          style={{ marginTop: 14, padding: 12, background: "var(--surface-subtle, #f7f8fa)", border: "0.5px solid var(--border)", borderRadius: 8 }}
          data-testid="admin-rc-recon-summary"
        >
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
            {result.dry_run ? "Preview" : "Result"} · {result.days_window}d · {result.reps_touched} rep{result.reps_touched === 1 ? "" : "s"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
            {[
              { k: "scanned", label: "Scanned" },
              { k: "patched", label: result.dry_run ? "Would patch" : "Patched" },
              { k: "already_correct", label: "Already correct" },
              { k: "no_rc_match", label: "No RC match" },
              { k: "skipped_no_token", label: "No token" },
              { k: "rc_fetch_errors", label: "RC errors" },
            ].map(({ k, label }) => (
              <div key={k} style={{ textAlign: "center" }} data-testid={`admin-rc-recon-${k}`}>
                <div style={{ fontSize: 18, fontWeight: 650 }}>{result[k] ?? 0}</div>
                <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{label}</div>
              </div>
            ))}
          </div>
          {(result.examples || []).length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, cursor: "pointer" }}>Example rows ({result.examples.length})</summary>
              <div style={{ maxHeight: 200, overflow: "auto", marginTop: 6, fontFamily: "ui-monospace, monospace", fontSize: 10, background: "#fff", padding: 8, borderRadius: 4 }}>
                {result.examples.map((ex, i) => (
                  <div key={i} style={{ padding: "3px 0", borderBottom: "0.5px dashed #eee" }}>
                    <code>{ex.external_id}</code> · was <b>{ex.was}s</b> → <b style={{ color: "#065f46" }}>{ex.now}s</b>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/* ── RC Audio Duration Probe Panel (Feb 2026) ────────────────────────
 * Reads the true audio-file duration from RC recordings and stamps
 * `recording_call_duration` on each activity_log row. Used when RC&apos;s
 * own `duration` field under-reports multi-leg / transferred / queued
 * calls. See services/audio_duration.py for the probe mechanics.
 * Endpoint: POST /api/admin/rc-recording-backfill/duration-from-audio
 * ────────────────────────────────────────────────────────────────── */
function RCAudioProbePanel({ token }) {
  const [days, setDays] = useState(14);
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState(200);
  const [agentId, setAgentId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const d = Number(days), l = Number(limit);
    if (!Number.isInteger(d) || d < 1 || d > 30) { toast.error("Days must be 1–30"); return; }
    if (!Number.isInteger(l) || l < 1 || l > 2000) { toast.error("Limit must be 1–2000"); return; }
    setRunning(true);
    setResult(null);
    try {
      const body = { days: d, dry_run: dryRun, limit: l };
      if (agentId.trim()) body.agent_id = agentId.trim();
      const r = await axios.post(
        `${API}/admin/rc-recording-backfill/duration-from-audio`,
        body, { headers: authHdr(token), timeout: 600000 }, // audio probes are network-heavy
      );
      setResult(r.data);
      toast.success(dryRun
        ? `Dry-run: ${r.data.patched} rows would be stamped`
        : `Stamped ${r.data.patched} rows with true audio duration`);
    } catch (e) {
      toast.error(pickErr(e, "Audio probe failed"));
    }
    setRunning(false);
  };

  return (
    <div
      style={{ marginTop: 20, paddingTop: 16, borderTop: "0.5px solid var(--border)" }}
      data-testid="admin-rc-audio-probe"
    >
      <h3 style={{ fontSize: 13, fontWeight: 650, marginBottom: 4 }}>Deduce Duration from Recording Audio</h3>
      <p style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 12 }}>
        Streams a small prefix of each RC recording and extracts the true audio-file
        duration into <code style={{ fontSize: 10 }}>recording_call_duration</code>. Use this when RC&apos;s call-log
        under-reports (multi-leg transfers, queue-routed calls). Rep Activity surfaces
        the deduced value as a chip next to Duration when it differs by more than 5s.
        Slower than the reconcile above — one HTTP probe per row.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "100px 100px 240px 1fr", gap: 12, alignItems: "end", marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Days (1–30)</label>
          <input
            type="number" min={1} max={30} className="input"
            value={days} onChange={e => setDays(e.target.value)}
            disabled={running}
            data-testid="admin-rc-probe-days"
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Limit (1–2000)</label>
          <input
            type="number" min={1} max={2000} className="input"
            value={limit} onChange={e => setLimit(e.target.value)}
            disabled={running}
            data-testid="admin-rc-probe-limit"
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Agent ID (optional)</label>
          <input
            className="input" value={agentId} onChange={e => setAgentId(e.target.value)}
            placeholder="Leave blank for all reps"
            disabled={running}
            data-testid="admin-rc-probe-agent"
          />
        </div>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox" checked={dryRun}
            onChange={e => setDryRun(e.target.checked)}
            disabled={running}
            data-testid="admin-rc-probe-dryrun"
          />
          Dry-run
        </label>
      </div>

      <button
        className="btn btn-primary" style={{ fontSize: 12 }}
        onClick={run} disabled={running}
        data-testid="admin-rc-probe-run"
      >
        {running ? "Probing…" : dryRun ? "Preview" : "Run audio probe"}
      </button>

      {result && (
        <div
          style={{ marginTop: 14, padding: 12, background: "var(--surface-subtle, #f7f8fa)", border: "0.5px solid var(--border)", borderRadius: 8 }}
          data-testid="admin-rc-probe-summary"
        >
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
            {result.dry_run ? "Preview" : "Result"} · {result.days_window}d · {result.reps_touched} rep{result.reps_touched === 1 ? "" : "s"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
            {[
              { k: "scanned", label: "Scanned" },
              { k: "probed", label: "Probed" },
              { k: "patched", label: result.dry_run ? "Would stamp" : "Stamped" },
              { k: "probe_failed", label: "Probe failed" },
              { k: "rate_limited", label: "RC 429" },
              { k: "skipped_no_url", label: "No URL" },
              { k: "skipped_no_token", label: "No token" },
            ].map(({ k, label }) => (
              <div key={k} style={{ textAlign: "center" }} data-testid={`admin-rc-probe-${k}`}>
                <div style={{ fontSize: 18, fontWeight: 650, color: (k === "rate_limited" && (result[k] || 0) > 0) ? "#c2410c" : undefined }}>{result[k] ?? 0}</div>
                <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{label}</div>
              </div>
            ))}
          </div>
          {result.aborted_by_429 && (
            <div data-testid="admin-rc-probe-abort-notice" style={{ marginTop: 8, padding: "8px 10px", background: "#fef3c7", border: "0.5px solid #f59e0b", borderRadius: 6, fontSize: 11, color: "#92400e" }}>
              ⚠ Sweep aborted after 3 consecutive RC rate-limit responses to protect the recording Play button.
              Wait ~60 seconds before retrying. You can also increase the throttle delay to reduce pressure.
            </div>
          )}
          {(result.examples || []).length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, cursor: "pointer" }}>Example rows ({result.examples.length})</summary>
              <div style={{ maxHeight: 240, overflow: "auto", marginTop: 6, fontFamily: "ui-monospace, monospace", fontSize: 10, background: "#fff", padding: 8, borderRadius: 4 }}>
                {result.examples.map((ex, i) => {
                  const diff = (ex.recording_call_duration || 0) - (ex.duration_sec || 0);
                  const flag = Math.abs(diff) > 5;
                  return (
                    <div key={i} style={{ padding: "3px 0", borderBottom: "0.5px dashed #eee" }}>
                      <code>{ex.external_id}</code>
                      <span style={{ marginLeft: 8 }}>RC: <b>{ex.duration_sec}s</b></span>
                      <span style={{ marginLeft: 8 }}>Audio: <b style={{ color: flag ? "#c2410c" : "#065f46" }}>{ex.recording_call_duration}s</b></span>
                      {flag && <span style={{ marginLeft: 8, color: "#c2410c", fontSize: 9, fontWeight: 700 }}>Δ{diff > 0 ? "+" : ""}{diff}s</span>}
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/* ── RC → EHI MySQL Bridge panel (Iter 188.38) ───────────────────────
 * Josh (2026-08-13) is migrating the ehi-client Mongo collections
 * from CSV uploads to a live MySQL 8 warehouse. This panel captures
 * host / port / user / password / database and provides a Test
 * Connection button that runs a live handshake + a SELECT VERSION()
 * + a COUNT(*) probe against Josh's four target tables.
 * ─────────────────────────────────────────────────────────────────── */
function EhiMysqlBridgePanel({ token }) {
  const [cfg, setCfg] = useState(null);
  const [host, setHost] = useState("");
  const [port, setPort] = useState(3306);
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [database, setDatabase] = useState("ccm_transactions");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/settings/ehi-mysql`, { headers: authHdr(token) })
      .then((r) => {
        if (cancel) return;
        setCfg(r.data);
        if (r.data?.configured) {
          setHost(r.data.host || "");
          setPort(r.data.port || 3306);
          setUser(r.data.user || "");
          setDatabase(r.data.database || "ccm_transactions");
          // Password is NEVER returned; leave the field empty. The
          // has_password flag tells us whether a stored password exists.
        }
      })
      .catch((e) => toast.error(pickErr(e, "Failed to load EHI MySQL config")));
    return () => { cancel = true; };
  }, [token]);

  const onSave = async () => {
    if (!host.trim() || !user.trim()) {
      toast.error("Host and user are required");
      return;
    }
    if (!cfg?.has_password && !password) {
      toast.error("Password is required for the first save");
      return;
    }
    setSaving(true);
    try {
      const body = {
        host: host.trim(),
        port: Number(port) || 3306,
        user: user.trim(),
        database: database.trim() || "ccm_transactions",
      };
      // Only send password when the admin actually typed one — empty
      // means "keep the currently stored password".
      if (password) body.password = password;
      await axios.post(`${API}/settings/ehi-mysql`, body, { headers: authHdr(token) });
      toast.success("EHI MySQL config saved");
      setPassword(""); // clear form field so it stays masked-empty
      // Refresh config state so `has_password` toggles from false→true.
      const r = await axios.get(`${API}/settings/ehi-mysql`, { headers: authHdr(token) });
      setCfg(r.data);
    } catch (e) {
      toast.error(pickErr(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // If the admin typed a password into the form, send it so they
      // can test-before-save; otherwise leave the body empty so the
      // stored password is used.
      const body = password ? { password } : {};
      const r = await axios.post(`${API}/settings/ehi-mysql/test`, body, { headers: authHdr(token) });
      setTestResult(r.data);
      if (r.data?.ok) {
        toast.success(`Connected to MySQL ${r.data.server_version || ""}`);
      } else {
        toast.error("Connection failed — see details below");
      }
    } catch (e) {
      toast.error(pickErr(e, "Test failed"));
      setTestResult({ ok: false, error: pickErr(e, "Test failed") });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-subtle,#eaecef)" }}
         data-testid="admin-ehi-mysql-panel">
      <h3 style={{ fontSize: 14, fontWeight: 650, marginBottom: 4 }}>EHI MySQL Bridge</h3>
      <p style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 14 }}>
        Live connection to the EHI EMR MySQL 8 warehouse. Powers the sync jobs
        that keep <code>ehiclient_providers</code>, <code>ehiclient_support_tickets</code>,
        <code>ehiclient_invoice_summary</code>, and <code>ehiclient_invoice_details</code> in sync
        with <code>ccm_transactions.*</code>. Password is encrypted at rest.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Host / IP</label>
          <input className="input" value={host} onChange={(e) => setHost(e.target.value)}
                 placeholder="e.g. 10.0.0.42 or db.ehiehr.internal"
                 data-testid="admin-ehi-mysql-host" disabled={saving} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Port</label>
          <input className="input" type="number" value={port} onChange={(e) => setPort(e.target.value)}
                 min={1} max={65535} placeholder="3306"
                 data-testid="admin-ehi-mysql-port" disabled={saving} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>User</label>
          <input className="input" value={user} onChange={(e) => setUser(e.target.value)}
                 placeholder="mysql_username"
                 data-testid="admin-ehi-mysql-user" disabled={saving} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>
            Password {cfg?.has_password && <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}>· stored, blank keeps</span>}
          </label>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                 placeholder={cfg?.has_password ? "•••••••• (unchanged)" : "password"}
                 autoComplete="new-password"
                 data-testid="admin-ehi-mysql-password" disabled={saving} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Database</label>
          <input className="input" value={database} onChange={(e) => setDatabase(e.target.value)}
                 placeholder="ccm_transactions"
                 data-testid="admin-ehi-mysql-database" disabled={saving} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={onSave}
                disabled={saving || testing} data-testid="admin-ehi-mysql-save">
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="btn" style={{ fontSize: 12 }} onClick={onTest}
                disabled={saving || testing}
                data-testid="admin-ehi-mysql-test">
          {testing ? "Testing…" : "Test connection"}
        </button>
        {cfg?.updated_at && (
          <span style={{ fontSize: 11, color: "var(--text-tertiary)", marginLeft: "auto", alignSelf: "center" }}
                data-testid="admin-ehi-mysql-updated-at">
            Last saved: {new Date(cfg.updated_at).toLocaleString()}
            {cfg.updated_by ? ` by ${cfg.updated_by}` : ""}
          </span>
        )}
      </div>

      {testResult && (
        <div style={{
          marginTop: 14, padding: 12, borderRadius: 6, fontSize: 12,
          background: testResult.ok ? "#f0fdf4" : "#fff1f0",
          border: `1px solid ${testResult.ok ? "#bbf7d0" : "#fecaca"}`,
        }} data-testid="admin-ehi-mysql-test-result">
          {testResult.ok ? (
            <>
              <div style={{ fontWeight: 650, color: "#166534", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <Check style={{ width: 12, height: 12 }} /> Connected in {testResult.elapsed_ms} ms
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>Server:</strong> MySQL {testResult.server_version} · <strong>DB:</strong> {testResult.database}
              </div>
              {testResult.tables && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Target tables:</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {Object.entries(testResult.tables).map(([tbl, info]) => (
                      <li key={tbl} style={{ marginBottom: 2 }}>
                        <code>{tbl}</code>{" "}
                        {info.ok ? (
                          <span style={{ color: "#166534" }}>✓ {info.row_count.toLocaleString()} rows</span>
                        ) : (
                          <span style={{ color: "#a32d2d" }}>✗ {info.error}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontWeight: 650, color: "#a32d2d", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <X style={{ width: 12, height: 12 }} /> Connection failed
                {typeof testResult.elapsed_ms === "number" ? ` (after ${testResult.elapsed_ms} ms)` : ""}
              </div>
              <div style={{ color: "#7f1d1d", fontFamily: "monospace", fontSize: 11 }}>
                {testResult.error}
              </div>
            </>
          )}
        </div>
      )}

      <EhiMysqlSyncPanel token={token} />
    </div>
  );
}

/* ── EHI MySQL Sync panel — Iter 188.39 ─────────────────────────────
 * Truncate-and-rebuild sync of the 4 ehiclient_* Mongo collections
 * from the paired MySQL tables. Runs daily at 07:00 UTC. Manual
 * trigger below is gated by the shared password "07936".
 * Renders a descending log of recent runs with per-table status.
 * ─────────────────────────────────────────────────────────────────── */
function EhiMysqlSyncPanel({ token }) {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState("");
  const [running, setRunning] = useState(false);

  const refreshLogs = async () => {
    try {
      const r = await axios.get(`${API}/settings/ehi-mysql/sync/logs`, { headers: authHdr(token) });
      setLogs(r.data?.logs || []);
    } catch (e) {
      // Non-fatal; the panel above already surfaces auth/config errors.
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    refreshLogs();
    // Auto-refresh every 5 s while a run is in progress so admins
    // see per-table completion land as it happens.
    const t = setInterval(() => {
      // Only refresh if something is 'running' — otherwise every 30 s
      // is enough (drives cadence when the panel is left open).
      const running = logs.some((l) => l.status === "running");
      if (running || Date.now() % 30000 < 5000) refreshLogs();
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitRun = async () => {
    if (!pwd) {
      toast.error("Enter the sync password");
      return;
    }
    setRunning(true);
    try {
      const r = await axios.post(`${API}/settings/ehi-mysql/sync/run`,
        { password: pwd }, { headers: authHdr(token) });
      if (r.data?.status === "ok") {
        toast.success("Sync completed for all 4 collections");
      } else if (r.data?.status === "partial_failure") {
        toast.warning("Sync completed with some failures — see log");
      } else {
        toast.error("Sync failed — see log for details");
      }
      setShowPwd(false);
      setPwd("");
      refreshLogs();
    } catch (e) {
      toast.error(pickErr(e, "Sync failed"));
    } finally {
      setRunning(false);
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  const statusStyle = (s) => {
    if (s === "ok") return { bg: "#dcfce7", fg: "#166534", label: "OK" };
    if (s === "running") return { bg: "#fef3c7", fg: "#92400e", label: "Running…" };
    if (s === "partial_failure") return { bg: "#ffedd5", fg: "#9a3412", label: "Partial" };
    return { bg: "#fee2e2", fg: "#991b1b", label: "Failed" };
  };

  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-subtle,#eaecef)" }}
         data-testid="admin-ehi-mysql-sync-panel">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 650, margin: 0 }}>EHI MySQL Sync</h3>
          <p style={{ fontSize: 11, color: "var(--text-tertiary)", margin: "4px 0 0" }}>
            Truncates + rebuilds the 4 <code>ehiclient_*</code> collections from MySQL. Auto-runs daily at 07:00 UTC. Manual runs require the sync password.
          </p>
        </div>
        <button className="btn btn-primary" style={{ fontSize: 12 }}
                onClick={() => setShowPwd(true)}
                disabled={running}
                data-testid="admin-ehi-mysql-sync-run">
          {running ? "Running…" : "Run sync now"}
        </button>
      </div>

      {showPwd && (
        <div style={{
          padding: 12, marginBottom: 10, borderRadius: 6,
          background: "#f8fafc", border: "1px solid #e2e8f0",
          display: "flex", gap: 8, alignItems: "center",
        }} data-testid="admin-ehi-mysql-sync-password-panel">
          <input className="input" type="password" value={pwd}
                 onChange={(e) => setPwd(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") submitRun(); }}
                 placeholder="Sync password" autoFocus
                 style={{ maxWidth: 220 }}
                 data-testid="admin-ehi-mysql-sync-password" />
          <button className="btn btn-primary" style={{ fontSize: 12 }}
                  onClick={submitRun} disabled={running}
                  data-testid="admin-ehi-mysql-sync-confirm">
            {running ? "Running…" : "Confirm & run"}
          </button>
          <button className="btn" style={{ fontSize: 12 }}
                  onClick={() => { setShowPwd(false); setPwd(""); }}
                  disabled={running}>
            Cancel
          </button>
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11 }} data-testid="admin-ehi-mysql-sync-logs">
        <div style={{ fontWeight: 600, marginBottom: 6, color: "#334155" }}>Recent sync runs</div>
        {loadingLogs ? (
          <div style={{ color: "#94a3b8" }}>Loading…</div>
        ) : logs.length === 0 ? (
          <div style={{ color: "#94a3b8" }}>No sync runs yet. Click "Run sync now" or wait for the 07:00 UTC schedule.</div>
        ) : (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Started</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Trigger</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: "right", padding: "6px 10px", fontWeight: 600 }}>Elapsed</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Per-table</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const st = statusStyle(log.status);
                  return (
                    <tr key={log._id} style={{ borderTop: "1px solid #e2e8f0" }}
                        data-testid={`admin-ehi-mysql-sync-log-${log._id}`}>
                      <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{fmt(log.started_at)}</td>
                      <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                        {log.source === "manual" ? `manual · ${log.triggered_by || "?"}` : log.source}
                      </td>
                      <td style={{ padding: "6px 10px" }}>
                        <span style={{ padding: "2px 8px", borderRadius: 4, background: st.bg, color: st.fg, fontWeight: 600 }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                        {log.elapsed_ms != null ? `${(log.elapsed_ms / 1000).toFixed(1)}s` : "—"}
                      </td>
                      <td style={{ padding: "6px 10px" }}>
                        {Array.isArray(log.tables) && log.tables.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {log.tables.map((t) => (
                              <div key={t.mysql_table} style={{ fontSize: 10, color: t.status === "ok" ? "#166534" : "#991b1b" }}>
                                <code>{t.mongo_collection}</code>{" "}
                                {t.status === "ok"
                                  ? `✓ ${(t.rows_inserted || 0).toLocaleString()} rows in ${((t.elapsed_ms || 0) / 1000).toFixed(1)}s`
                                  : `✗ ${(t.error || "").slice(0, 100)}`}
                              </div>
                            ))}
                          </div>
                        ) : <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={{ padding: "6px 10px", color: "#991b1b", fontFamily: "monospace", fontSize: 10, maxWidth: 240, wordBreak: "break-word" }}>
                        {log.error_summary || ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── RC → Backfill Auto-match sub-panel ──────────────────────────── */
function BackfillAutoMatchPanel({ token }) {
  const [days, setDays] = useState(90);
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState(1000);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);

  const run = async () => {
    // Validate client-side to match server bounds (1..365 / 1..10000).
    const d = Number(days), l = Number(limit);
    if (!Number.isInteger(d) || d < 1 || d > 365) {
      toast.error("Days must be an integer between 1 and 365"); return;
    }
    if (!Number.isInteger(l) || l < 1 || l > 10000) {
      toast.error("Limit must be an integer between 1 and 10000"); return;
    }
    setRunning(true);
    setSummary(null);
    try {
      const r = await axios.post(
        `${API}/admin/rc/backfill-auto-match`,
        { days: d, dry_run: dryRun, limit: l },
        { headers: authHdr(token) },
      );
      setSummary(r.data);
      toast.success(dryRun
        ? `Dry-run complete — ${r.data.matched} would link`
        : `Backfill complete — ${r.data.linked} linked`);
    } catch (e) {
      toast.error(pickErr(e, "Backfill failed"));
    }
    setRunning(false);
  };

  return (
    <div
      style={{ marginTop: 20, paddingTop: 16, borderTop: "0.5px solid var(--border)" }}
      data-testid="admin-rc-backfill"
    >
      <h3 style={{ fontSize: 13, fontWeight: 650, marginBottom: 4 }}>Backfill Auto-match</h3>
      <p style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 12 }}>
        Retroactively link orphan RC activity rows by exact phone match. Dry-run
        shows counts without writing. Rows with multiple matches are flagged as
        ambiguous and left to the manual “Match to Lead” chip.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "120px 120px 1fr", gap: 12, alignItems: "end", marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Days (1–365)</label>
          <input
            type="number" min={1} max={365} className="input"
            value={days} onChange={e => setDays(e.target.value)}
            disabled={running}
            data-testid="admin-rc-backfill-days"
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Limit (1–10000)</label>
          <input
            type="number" min={1} max={10000} className="input"
            value={limit} onChange={e => setLimit(e.target.value)}
            disabled={running}
            data-testid="admin-rc-backfill-limit"
          />
        </div>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox" checked={dryRun}
            onChange={e => setDryRun(e.target.checked)}
            disabled={running}
            data-testid="admin-rc-backfill-dryrun"
          />
          Dry-run (no writes)
        </label>
      </div>

      <button
        className="btn btn-primary" style={{ fontSize: 12 }}
        onClick={run} disabled={running}
        data-testid="admin-rc-backfill-run"
      >
        {running ? "Running…" : dryRun ? "Run dry-run" : "Run backfill"}
      </button>

      {summary && (
        <div
          style={{ marginTop: 14, padding: 12, background: "var(--surface-subtle, #f7f8fa)", border: "0.5px solid var(--border)", borderRadius: 8 }}
          data-testid="admin-rc-backfill-summary"
        >
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
            {summary.dry_run ? "Dry-run result" : "Backfill result"} · {summary.days}d · cap {summary.limit}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
            {[
              { k: "scanned", label: "Scanned" },
              { k: "matched", label: "Matched" },
              { k: "linked", label: "Linked" },
              { k: "ambiguous", label: "Ambiguous" },
              { k: "no_match", label: "No match" },
            ].map(({ k, label }) => (
              <div key={k} style={{ textAlign: "center" }} data-testid={`admin-rc-backfill-${k}`}>
                <div style={{ fontSize: 18, fontWeight: 650 }}>{summary[k]}</div>
                <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>{label}</div>
              </div>
            ))}
          </div>
          {summary.dry_run && summary.matched > 0 && (
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 8 }}>
              Uncheck <strong>Dry-run</strong> and re-run to persist links.
            </div>
          )}
        </div>
      )}

      <BackfillHistoryPanel token={token} refreshKey={summary?.ran_at} />
    </div>
  );
}

/* ── RC → Backfill History sub-panel ─────────────────────────────── */
function BackfillHistoryPanel({ token, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rc/backfill-history?limit=10`, { headers: authHdr(token) });
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(pickErr(e, "Failed to load backfill history"));
      setRows([]);
    }
    setLoading(false);
  };

  // Auto-load on mount + refetch whenever a new run lands (refreshKey changes).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load() defined above; only refetch on refreshKey change
  useEffect(() => { load(); }, [refreshKey]);

  return (
    <div
      style={{ marginTop: 20, paddingTop: 16, borderTop: "0.5px solid var(--border)" }}
      data-testid="admin-rc-backfill-history"
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 650 }}>Backfill History</h3>
          <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Last 10 runs · newest first</p>
        </div>
        <button
          className="btn" style={{ fontSize: 11 }}
          onClick={load} disabled={loading}
          data-testid="admin-rc-backfill-history-refresh"
        >
          <RefreshCw style={{ width: 12, height: 12 }} /> Refresh
        </button>
      </div>

      {rows === null || loading ? (
        <div style={{ padding: 16, fontSize: 12, color: "var(--text-tertiary)" }} data-testid="admin-rc-backfill-history-loading">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{ padding: 16, fontSize: 12, color: "var(--text-tertiary)", textAlign: "center", border: "0.5px dashed var(--border)", borderRadius: 6 }}
          data-testid="admin-rc-backfill-history-empty"
        >
          No backfill runs yet.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: "0.5px solid var(--border)" }}>
                {["Time", "Actor", "Mode", "Days / Limit", "Scanned", "Matched", "Linked", "Ambiguous", "No match"].map(h => (
                  <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.id}
                  style={{ borderBottom: "0.5px solid var(--border)" }}
                  data-testid={`admin-rc-backfill-history-row-${r.id}`}
                >
                  <td style={{ padding: "6px 8px", color: "var(--text-secondary)" }}>
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  <td style={{ padding: "6px 8px" }}>{r.actor_name || r.actor_id || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                      background: r.dry_run ? "var(--gray-light)" : "var(--green-light, #DAFBE8)",
                      color: r.dry_run ? "var(--text-secondary)" : "var(--green, #1D9E75)",
                    }}>
                      {r.dry_run ? "Dry-run" : "Live"}
                    </span>
                  </td>
                  <td style={{ padding: "6px 8px", color: "var(--text-secondary)" }}>{r.days}d / {r.limit}</td>
                  <td style={{ padding: "6px 8px" }}>{r.scanned ?? "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{r.matched ?? "—"}</td>
                  <td style={{ padding: "6px 8px", fontWeight: r.linked ? 600 : 400 }}>{r.linked ?? "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{r.ambiguous ?? "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{r.no_match ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Section 3: Feature Flags ────────────────────────────────────── */
function FeatureFlagsSection({ token }) {
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/flags`, { headers: authHdr(token) });
      setFlags(r.data);
    } catch (e) {
      toast.error(pickErr(e, "Failed to load flags"));
    }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load() defined above; intentional single-shot mount fetch
  useEffect(() => { load(); }, []);

  const update = async (key, value) => {
    const prev = flags;
    setFlags(f => ({ ...f, [key]: value }));
    setSavingKey(key);
    try {
      const r = await axios.patch(`${API}/admin/flags`, { [key]: value }, { headers: authHdr(token) });
      setFlags(r.data);
      toast.success(`${key} saved`);
    } catch (e) {
      setFlags(prev); // rollback
      toast.error(pickErr(e, "Flag save failed"));
    }
    setSavingKey(null);
  };

  if (loading || !flags) return <div className="card" style={{ padding: 32, textAlign: "center", color: "#888" }} data-testid="admin-flags-loading">Loading…</div>;

  const boolFlag = (key, label, desc) => (
    <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{desc}</div>
      </div>
      <button
        onClick={() => update(key, !flags[key])}
        disabled={savingKey === key}
        data-testid={`admin-flag-${key}`}
        style={{ width: 40, height: 22, borderRadius: 11, background: flags[key] ? "var(--blue)" : "var(--gray-light)", border: "none", cursor: savingKey === key ? "wait" : "pointer", position: "relative", transition: "background 0.2s" }}
      >
        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: flags[key] ? 20 : 2, transition: "left 0.2s" }} />
      </button>
    </div>
  );

  return (
    <div className="card" style={{ padding: "20px 24px" }} data-testid="admin-ops-flags">
      <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Feature Flags</h2>
      {boolFlag("demo_mode_enabled", "Demo Mode", "Allow the Try-Demo-Mode login flow")}
      {boolFlag("rc_enabled", "RingCentral Integration", "Master switch for RC calls/SMS")}
      {boolFlag("auto_match_rc_activity", "Auto-match RC activity", "When enabled, new RC calls/SMS auto-link to a lead by exact phone match (ambiguous rows stay manual)")}
      {boolFlag("meeting_auto_tasks_enabled", "Meeting auto tasks", "Auto-create follow-up tasks on meeting completed/no_show events.")}
      <div style={{ padding: "12px 0" }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>AI Override</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 8 }}>
          Force AI on/off. Leave as “env default” to honor the ENABLE_AI environment variable.
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { v: null, label: "Env default" },
            { v: true, label: "Force ON" },
            { v: false, label: "Force OFF" },
          ].map(opt => (
            <button
              key={String(opt.v)}
              onClick={() => update("ai_enabled_override", opt.v)}
              disabled={savingKey === "ai_enabled_override"}
              data-testid={`admin-flag-ai-${opt.v === null ? "env" : opt.v}`}
              style={{
                padding: "5px 12px", fontSize: 11, borderRadius: 6,
                border: "0.5px solid var(--border)",
                background: flags.ai_enabled_override === opt.v ? "var(--blue)" : "transparent",
                color: flags.ai_enabled_override === opt.v ? "#fff" : "var(--text-secondary)",
                cursor: savingKey === "ai_enabled_override" ? "wait" : "pointer",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {flags.updated_at && (
        <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 10 }}>Last updated: {flags.updated_at}</div>
      )}
    </div>
  );
}

/* ── Section 4: Secrets Health ───────────────────────────────────── */
function SecretsHealthSection({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/secrets-health`, { headers: authHdr(token) });
      setData(r.data);
    } catch (e) {
      toast.error(pickErr(e, "Failed to load secrets health"));
    }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load() defined above; intentional single-shot mount fetch
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="card" style={{ padding: 32, textAlign: "center", color: "#888" }} data-testid="admin-secrets-loading">Loading…</div>;

  return (
    <div className="card" style={{ padding: "20px 24px" }} data-testid="admin-ops-secrets">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 650 }}>Secrets Health</h2>
          <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Presence only — values are never sent to the browser.</p>
        </div>
        <button className="btn" style={{ fontSize: 11 }} onClick={load} data-testid="admin-secrets-refresh">
          <RefreshCw style={{ width: 12, height: 12 }} /> Refresh
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {data.items.map(item => (
          <div key={item.key} style={{ padding: "12px 14px", border: "0.5px solid var(--border)", borderRadius: 8 }} data-testid={`admin-secret-${item.key}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "monospace" }}>{item.key}</span>
              <span
                data-testid={`admin-secret-status-${item.key}`}
                style={{
                  fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                  background: item.configured ? "#DAFBE8" : "#FFF1F0",
                  color: item.configured ? "#1D9E75" : "#A32D2D",
                  display: "inline-flex", alignItems: "center", gap: 3,
                }}
              >
                {item.configured ? <Check style={{ width: 10, height: 10 }} /> : <X style={{ width: 10, height: 10 }} />}
                {item.configured ? "configured" : "missing"}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>{item.hint}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 12 }}>Checked at {data.checked_at}</div>
    </div>
  );
}

/* ── Main export: the admin-ops tab body ─────────────────────────── */
/* ── Section 5: Public Site Walkthrough Bookings ─────────────────── */
const WT_STATUSES = ["New", "Contacted", "Converted", "Spam"];
const wtTone = (s) => ({ New: { bg: "#eff6ff", fg: "#2563eb" }, Contacted: { bg: "#fffbeb", fg: "#b45309" }, Converted: { bg: "#ecfdf5", fg: "#059669" }, Spam: { bg: "#fef2f2", fg: "#b91c1c" } }[s] || { bg: "#f1f5f9", fg: "#64748b" });

function WalkthroughBookingsSection({ token }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sel, setSel] = useState([]);
  const [detail, setDetail] = useState(null);
  const [reps, setReps] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [revealKey, setRevealKey] = useState(false);
  const [recap, setRecap] = useState({ recaptcha_enabled: false, recaptcha_secret_set: false });
  const [secretInput, setSecretInput] = useState("");
  const pageSize = 25;
  const H = { headers: authHdr(token) };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("page", page); params.set("page_size", pageSize);
      const r = await axios.get(`${API}/admin/walkthrough-bookings?${params}`, H);
      setRows(r.data.rows || []); setTotal(r.data.total || 0);
    } catch (e) { toast.error(pickErr(e, "Failed to load bookings")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, page]);
  useEffect(() => {
    axios.get(`${API}/users/directory`, H).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.users || r.data.rows || []);
      setReps(list);
    }).catch(() => {});
    axios.get(`${API}/admin/walkthrough-bookings/api-key`, H).then((r) => setApiKey(r.data.api_key)).catch(() => {});
    axios.get(`${API}/admin/walkthrough-bookings/settings`, H).then((r) => setRecap(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const repName = (id) => { const u = reps.find((x) => x.id === id); return u ? (u.name || u.email) : ""; };
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const patch = async (id, body) => {
    try { const r = await axios.patch(`${API}/admin/walkthrough-bookings/${id}`, body, H); toast.success("Saved"); setDetail(r.data); load(); }
    catch (e) { toast.error(pickErr(e, "Save failed")); }
  };

  const bulk = async (action, extra = {}) => {
    if (sel.length === 0) return toast.error("Select rows first");
    if (action === "mark_spam" && !window.confirm(`Mark ${sel.length} submission(s) as Spam?`)) return;
    try { const r = await axios.post(`${API}/admin/walkthrough-bookings/bulk`, { ids: sel, action, ...extra }, H); toast.success(`Updated ${r.data.modified}`); setSel([]); load(); }
    catch (e) { toast.error(pickErr(e, "Bulk action failed")); }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    axios.get(`${API}/admin/walkthrough-bookings/export?${params}`, { ...H, responseType: "blob" })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a"); a.href = url; a.download = "walkthrough-bookings.csv"; a.click(); window.URL.revokeObjectURL(url);
      }).catch((e) => toast.error(pickErr(e, "Export failed")));
  };

  const rotateKey = async () => {
    if (!window.confirm("Rotate the API key? The old key stops working immediately.")) return;
    try { const r = await axios.post(`${API}/admin/walkthrough-bookings/api-key/rotate`, {}, H); setApiKey(r.data.api_key); setRevealKey(true); toast.success("API key rotated"); }
    catch (e) { toast.error(pickErr(e, "Rotate failed")); }
  };

  const saveRecap = async (body) => {
    try { const r = await axios.put(`${API}/admin/walkthrough-bookings/settings`, body, H); setRecap(r.data); setSecretInput(""); toast.success("Saved"); }
    catch (e) { toast.error(pickErr(e, "Save failed")); }
  };

  const createLead = async (b) => {
    if (!window.confirm(`Create a Lead from ${b.first_name} ${b.last_name} (${b.practice_name})?`)) return;
    try {
      const full = `${b.first_name} ${b.last_name}`.trim();
      const contact = await axios.post(`${API}/contacts`, {
        first_name: b.first_name, last_name: b.last_name, email: b.email, phone: b.phone,
        company: b.practice_name, industry: "Healthcare", notes: `Address: ${b.practice_address}`,
        source: "Public Site Walkthrough Form", status: "active",
      }, H);
      const lead = await axios.post(`${API}/leads`, {
        contact_id: contact.data.id, title: b.practice_name || full, value: 0, stage: "new_lead",
        probability: 10, contact_name: full, company: b.practice_name,
        email: b.email, phone: b.phone, lead_source: "public_site_walkthrough_form",
        notes: `Walkthrough request. Address: ${b.practice_address}`,
      }, H);
      await axios.post(`${API}/admin/walkthrough-bookings/${b.id}/link-lead`, { lead_id: lead.data.id }, H);
      toast.success("Lead created & submission converted");
      setDetail(null); load();
    } catch (e) { toast.error(pickErr(e, "Create Lead failed")); }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const maskedKey = apiKey ? apiKey.slice(0, 6) + "•".repeat(Math.max(0, apiKey.length - 10)) + apiKey.slice(-4) : "—";

  return (
    <div data-testid="walkthrough-section">
      {/* API key + settings */}
      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <KeyRound style={{ width: 16, height: 16, color: "#2563eb" }} />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 650 }}>Marketing Site API Key</h3>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 0 }}>Share with the marketing team. Endpoint: <code>POST /api/public/walkthrough-bookings</code> with header <code>X-API-Key</code>.</p>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <code data-testid="wt-api-key" style={{ background: "#0f172a", color: "#e2e8f0", padding: "7px 12px", borderRadius: 8, fontSize: 12.5 }}>{revealKey ? apiKey : maskedKey}</code>
          <button className="btn" data-testid="wt-reveal-key" onClick={() => setRevealKey((v) => !v)}>{revealKey ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />} {revealKey ? "Hide" : "Reveal"}</button>
          <button className="btn" data-testid="wt-rotate-key" onClick={rotateKey}><RefreshCw style={{ width: 14, height: 14 }} /> Rotate</button>
        </div>
        <div style={{ borderTop: "1px solid var(--border-color, #e5e7eb)", marginTop: 14, paddingTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 600 }}>
            <input type="checkbox" data-testid="wt-recaptcha-toggle" checked={!!recap.recaptcha_enabled} onChange={(e) => saveRecap({ recaptcha_enabled: e.target.checked })} />
            Enable server-side Google reCAPTCHA verification {recap.recaptcha_secret_set ? "" : "(secret required)"}
          </label>
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input data-testid="wt-recaptcha-secret" value={secretInput} onChange={(e) => setSecretInput(e.target.value)} placeholder="Paste reCAPTCHA v2 secret key" style={{ padding: "7px 10px", border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, fontSize: 12.5, minWidth: 280 }} />
            <button className="btn" data-testid="wt-save-secret" disabled={!secretInput.trim()} onClick={() => saveRecap({ recaptcha_secret: secretInput.trim() })}>Save secret</button>
            <span style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>{recap.recaptcha_secret_set ? "Secret is set." : "No secret set — verification stays off (marketing site is the gate)."}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select data-testid="wt-filter-status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} style={{ padding: "7px 10px", border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, fontSize: 12.5 }}>
            <option value="">All statuses</option>
            {WT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, padding: "0 8px" }}>
            <Search style={{ width: 14, height: 14, color: "#94a3b8" }} />
            <input data-testid="wt-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }} placeholder="Search email or practice" style={{ border: "none", outline: "none", padding: "7px 2px", fontSize: 12.5, minWidth: 200 }} />
          </div>
          <input type="date" data-testid="wt-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: "6px 8px", border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, fontSize: 12.5 }} />
          <input type="date" data-testid="wt-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: "6px 8px", border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, fontSize: 12.5 }} />
          <button className="btn" data-testid="wt-apply-filters" onClick={() => { setPage(1); load(); }}>Apply</button>
          <div style={{ flex: 1 }} />
          <button className="btn" data-testid="wt-export" onClick={exportCsv}><Download style={{ width: 14, height: 14 }} /> Export CSV</button>
        </div>
        {sel.length > 0 && (
          <div data-testid="wt-bulk-bar" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, background: "#eff6ff", padding: "8px 12px", borderRadius: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{sel.length} selected</span>
            <select data-testid="wt-bulk-assign" defaultValue="" onChange={(e) => { if (e.target.value) bulk("assign", { assigned_rep_id: e.target.value }); e.target.value = ""; }} style={{ padding: "5px 8px", borderRadius: 8, fontSize: 12 }}>
              <option value="">Assign to…</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
            </select>
            <button className="btn" data-testid="wt-bulk-spam" onClick={() => bulk("mark_spam")}>Mark Spam</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="wt-empty" style={{ padding: 40, textAlign: "center" }}>
            <Inbox style={{ width: 30, height: 30, color: "#cbd5e1", margin: "0 auto" }} />
            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600 }}>No submissions yet</div>
            <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 4 }}>Walkthrough requests from the marketing site will appear here.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table data-testid="wt-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={wtTh}><input type="checkbox" data-testid="wt-select-all" checked={sel.length === rows.length} onChange={(e) => setSel(e.target.checked ? rows.map((r) => r.id) : [])} /></th>
                <th style={wtTh}>Submitted</th><th style={wtTh}>Name</th><th style={wtTh}>Email</th><th style={wtTh}>Phone</th><th style={wtTh}>Practice</th><th style={wtTh}>Status</th><th style={wtTh}>Assigned</th><th style={wtTh}></th>
              </tr></thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} data-testid={`wt-row-${b.id}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={wtTd}><input type="checkbox" data-testid={`wt-select-${b.id}`} checked={sel.includes(b.id)} onChange={() => toggleSel(b.id)} /></td>
                    <td style={wtTd}>{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</td>
                    <td style={wtTd}>{b.first_name} {b.last_name}</td>
                    <td style={wtTd}>{b.email}</td>
                    <td style={wtTd}>{b.phone}</td>
                    <td style={wtTd}>{b.practice_name}</td>
                    <td style={wtTd}><span style={{ ...pillStyle(wtTone(b.status)) }}>{b.status}</span></td>
                    <td style={wtTd}>{b.assigned_rep_name || "—"}</td>
                    <td style={wtTd}><button className="btn" data-testid={`wt-open-${b.id}`} onClick={() => setDetail(b)}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end", marginTop: 10, fontSize: 12 }}>
          <button className="btn" data-testid="wt-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} / {pages}</span>
          <button className="btn" data-testid="wt-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}

      {/* Detail side panel */}
      {detail && (
        <div data-testid="wt-detail" onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 60, display: "flex", justifyContent: "flex-end" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: "94vw", background: "#fff", height: "100%", overflow: "auto", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>{detail.first_name} {detail.last_name}</h3>
              <button className="btn" data-testid="wt-detail-close" onClick={() => setDetail(null)}><X style={{ width: 14, height: 14 }} /></button>
            </div>
            {[["Email", detail.email], ["Phone", detail.phone], ["Practice", detail.practice_name], ["Address", detail.practice_address], ["IP", detail.ip], ["User-agent", detail.user_agent], ["Submitted", detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"], ["Lead", detail.lead_id || "—"]].map(([k, v]) => (
              <div key={k} style={{ marginBottom: 8 }}><div style={{ fontSize: 10.5, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</div><div style={{ fontSize: 12.5, wordBreak: "break-word" }}>{v || "—"}</div></div>
            ))}
            <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 12, paddingTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4 }}>STATUS</div>
              <select data-testid="wt-detail-status" value={detail.status} onChange={(e) => patch(detail.id, { status: e.target.value })} style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>
                {WT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4 }}>ASSIGN REP</div>
              <select data-testid="wt-detail-assign" value={detail.assigned_rep_id || ""} onChange={(e) => patch(detail.id, { assigned_rep_id: e.target.value || null })} style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>
                <option value="">Unassigned</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
              </select>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4 }}>NOTE</div>
              <textarea data-testid="wt-detail-note" defaultValue={detail.note || ""} rows={4} onBlur={(e) => { if (e.target.value !== (detail.note || "")) patch(detail.id, { note: e.target.value }); }} style={{ width: "100%", padding: 10, border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box" }} />
              <button className="btn btn-primary" data-testid="wt-create-lead" style={{ marginTop: 12, width: "100%" }} disabled={detail.status === "Converted"} onClick={() => createLead(detail)}>
                <UserPlus style={{ width: 14, height: 14 }} /> {detail.status === "Converted" ? "Already converted" : "Create Lead"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const wtTh = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", padding: "10px 12px", whiteSpace: "nowrap" };
const wtTd = { fontSize: 12.5, padding: "9px 12px", whiteSpace: "nowrap" };
const pillStyle = (t) => ({ background: t.bg, color: t.fg, padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600 });

export default function AdminOps({ subSection }) {
  const { user, token } = useAuth();
  const role = user?.role;
  if (role !== "super_admin") {
    return (
      <div className="card" style={{ padding: 32, textAlign: "center" }} data-testid="admin-ops-forbidden">
        <Shield style={{ width: 32, height: 32, color: "var(--text-tertiary)", margin: "0 auto" }} />
        <div style={{ marginTop: 12, fontSize: 13, fontWeight: 600 }}>Super Admin only</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>
          Ask a super_admin to access this panel.
        </div>
      </div>
    );
  }

  if (subSection === "ops_rc") return <RCOperationsSection token={token} />;
  if (subSection === "ops_flags") return <FeatureFlagsSection token={token} />;
  if (subSection === "ops_secrets") return <SecretsHealthSection token={token} />;
  if (subSection === "ops_walkthrough") return <WalkthroughBookingsSection token={token} />;
  // Default fallback — surface RC Ops since ops_users was folded into
  // Settings → Users & Access → Team Members as part of the Feb 2026 IA
  // cleanup. Any caller passing the legacy `ops_users` value is handled
  // upstream by the Settings redirect map (no-op here, but defensive).
  return <RCOperationsSection token={token} />;
}

export const ADMIN_OPS_SECTIONS = [
  { key: "ops_rc", label: "RC Operations", icon: Zap, roles: ["super_admin"] },
  { key: "ops_flags", label: "Feature Flags", icon: ToggleRight, roles: ["super_admin"] },
  { key: "ops_walkthrough", label: "Walkthrough Requests", icon: Inbox, roles: ["super_admin"] },
  // P0-C April 2026: "Secrets Health" removed from the Settings IA per
  // user-feedback ("too low-level for a sales tool"). The /settings?
  // section=ops_secrets URL still resolves (handler at line ~533), so
  // any operator who bookmarked it directly still has access — we just
  // don't surface it in the nav.
];
